import React, { useState, useEffect, useRef, useCallback } from 'react';
import OktaSignIn from '@okta/okta-signin-widget';
import cloneDeep from 'lodash/cloneDeep';
import { REDIRECT_STORAGE_KEY, oktaWidgetConfiguration, oktaWidgetID, IDP_DISCOVERY, PRIMARY_AUTH } from '../../okta';
import MonitorErrorImage from '../../assets/MonitorErrorImage';
import cn from 'classnames';
import qs from 'query-string';
import { useHistory } from 'react-router-dom';
import { ROUTES } from '../../navigation/router';
import logger from '../../utils/logger';
import { useIsMounted } from '../../utils/hooks';
import { triggerIDPDiscovery } from '../../utils/ehr-launch-utils';
import envConfig from '../../config/config';

import './okta-theme.scss';
import typography from '@tcl-boron-styles/typography/dist/index.module.css';
import '../../shared/styles.scss';

const PRIVACY_URL = 'https://www.tempus.com/privacypolicy/';
const TERMS_URL = 'https://www.tempus.com/providersponsor-termsofuse/';

export const oktaWidgetInstance = initializeOktaWidget();

export default function OktaWidgetWrapper() {
  const history = useHistory();
  const widgetRef = useRef(null);
  const isMounted = useIsMounted();

  const [error, setError] = useState(undefined);

  const renderTroubleLoggingIn = useCallback(() => {
    const TEXT = 'Trouble logging in?';
    const submitLink = document.querySelector('#okta-signin-submit');

    // Render 'Trouble logging in?' button below 'Log in'
    if (submitLink) {
      const div = document.createElement('div');

      div.innerHTML = `
      <div class="custom-button trouble-logging-in-button" data-testid="trouble-logging-in-button" id="trouble-logging-in-button"
      >
        ${TEXT}
      </div>`;
      div.onclick = () => {
        history.push(ROUTES.TROUBLE_LOGGING_IN);
      };
      submitLink.parentElement.appendChild(div);
    }
  }, [history]);

  const renderDisclaimer = useCallback((_, isActivationPage) => {
    const DEFAULT_MESSAGE = 'By logging in';
    const NEW_ACCOUNT_MESSAGE = 'By creating an account';
    const submitLink = document.querySelector('#okta-signin-submit');

    // Render disclaimer below 'Trouble logging in?' button
    if (submitLink) {
      const div = document.createElement('div');
      const actionText = isActivationPage ? NEW_ACCOUNT_MESSAGE : DEFAULT_MESSAGE;

      div.innerHTML = `
      <div class="terms-disclaimer-container" data-testid="terms-disclaimer-container" style="text-align:center" id="login-disclaimer">
        ${actionText}, you agree to our <a class="legal-disclaimer-link" target="_blank" href="${TERMS_URL}" style="color:#206ef3">Terms of Use</a> and
        <a class="legal-disclaimer-link" target="_blank" href="${PRIVACY_URL}" style="color:#206ef3">Privacy Policy</a>
      </div>`; //ActionPalette300=#206ef3
      submitLink.parentElement.appendChild(div);
    }
  }, []);

  const clearAllCustomComponents = useCallback(() => {
    const renderedDomElements = {
      troubleLoggingIn: document.getElementById('trouble-logging-in-button'),
      disclaimer: document.getElementById('login-disclaimer'),
    };

    Object.keys(renderedDomElements).forEach((key) => {
      if (renderedDomElements[key]) {
        renderedDomElements[key]?.remove();
      }
    });
  }, []);

  const renderAllCustomComponents = useCallback(
    (context, isActivationPage) => {
      renderDisclaimer(context, isActivationPage);
    },
    [renderDisclaimer],
  );

  useEffect(() => {
    if (!widgetRef.current) {
      return;
    }
    oktaWidgetInstance.showSignInAndRedirect({ el: oktaWidgetID });

    clearAllCustomComponents();

    oktaWidgetInstance.on('afterRender', (context) => {
      const { activationToken } = qs.parse(window.location.search);
      const isActivationPage = !!activationToken;

      if (isMounted()) {
        const contextMap = {
          // Page with Email and Password fields
          [PRIMARY_AUTH]: () => renderAllCustomComponents(context, isActivationPage),
          // Page with just Email field
          [IDP_DISCOVERY]: () => renderTroubleLoggingIn(),
        };

        // Handle EHR launch IDP Discovery, needs to be done after the widget is rendered
        const queryObj = qs.parse(window.location.search);
        const hasFromEHRQueryParam = queryObj?.fromEHR;
        const hasFromLoginQueryParam = queryObj?.fromLogin;

        // Only trigger IDP discovery if we have fromEHR but NOT fromLogin
        // fromLogin means we're returning from SSO, so we should complete auth instead
        if (hasFromEHRQueryParam && !hasFromLoginQueryParam) {
          triggerIDPDiscovery();
        }

        return contextMap[context.controller]() ?? null;
      }
    });

    oktaWidgetInstance.on('afterError', (context, error) => {
      if (!isMounted()) {
        logger.error('Error loading signin widget', { error, context });
        setError('Error loading signin widget');
      }
    });

    return () => {
      removeWidget();
    };
  }, [widgetRef, clearAllCustomComponents, isMounted, renderAllCustomComponents, renderTroubleLoggingIn]);

  if (error)
    return (
      <div className="app-view-container" data-test-id="login-error-container" style={{ textAlign: 'center' }}>
        <MonitorErrorImage />
        <h4 className={cn(typography.H4)}>SOMETHING WENT WRONG</h4>
        <p>
          There was an issue loading this page.
          <br />
          Try refreshing the page or contact us to resolve the issue.
        </p>
        <p>
          <a className={cn(typography.link)} href="mailto:support@tempus.com">
            Contact Tempus support
          </a>
        </p>
      </div>
    );

  return (
    <React.Fragment>
      <div id={oktaWidgetID.replace('#', '')} data-testid={oktaWidgetID.replace('#', '')} ref={widgetRef} />
    </React.Fragment>
  );
}

export function isLoginRedirect() {
  return oktaWidgetInstance.authClient.isLoginRedirect();
}

export async function getWithRedirect(params) {
  await oktaWidgetInstance.authClient.token.getWithRedirect(params);
}

export function removeWidget() {
  oktaWidgetInstance.remove();
}

export function hideWidget() {
  oktaWidgetInstance.hide();
}

export async function showSignInAndRedirect() {
  await oktaWidgetInstance.showSignInAndRedirect();
}

export function initializeOktaWidget() {
  // Allow authorized clients to opt in to oauth implicit flow.
  const { recoveryToken, activationToken } = qs.parse(window.location.search);

  const oktaSettings = cloneDeep(oktaWidgetConfiguration);

  if (recoveryToken) {
    oktaSettings.recoveryToken = recoveryToken;
  }

  if (activationToken) {
    oktaSettings.recoveryToken = activationToken;
    oktaSettings.i18n.en['password.reset.title.generic'] = 'Set your password';
    oktaSettings.i18n.en['password.reset'] = 'Set Password';
  }

  // Add custom help link when no redirect or hub redirect
  const redirect = window.sessionStorage.getItem(REDIRECT_STORAGE_KEY);
  if (!redirect || redirect.includes(envConfig.apps.clinicalPortal)) {
    oktaSettings.helpLinks = {
      custom: [
        {
          text: 'Request Access',
          href: 'https://go.tempus.com/access-request',
        },
        {
          text: 'Trouble logging in?',
          href: `${window.location.origin}/trouble-logging-in`,
        },
      ],
    };
  }

  const oktaWidgetInstance = new OktaSignIn(oktaSettings);

  return oktaWidgetInstance;
}
